import { usePublishedData } from "../lib/published-data";
import { createFileRoute, redirect, useLocation } from "@tanstack/react-router";
import { DocumentContent } from "../components/document-content";
import { CreatorHome } from "../components/home/creator-home";
import { homeStylesheets } from "../components/home/stylesheets";
import { documentHead, useShell } from "../lib/document-route";
import { withStylesheet } from "../lib/head";
import {
  PlatformHome,
  homeFlow,
  loadHome as loadPlatformHome,
  platformHomeHead,
} from "../platform/home-route";
import { isPlatformShell } from "../platform/shell";
import { loadHome } from "../server/home";

/**
 * Home: the creator's published Home page, or the native home built from their template. On
 * Drate's own host the marketing home takes this route.
 */
export const Route = createFileRoute("/")({
  // Drate's homepage tells one story per ?flow=; a change of story loads that story's page.
  loaderDeps: ({ search }) => ({ flow: homeFlow(search) }),
  loader: async ({ deps, parentMatchPromise }) => {
    // A browser already holds the shell and knows Drate's own host without asking. The server
    // doesn't wait for the shell: loadHome answers "platform" at once there, and a creator's
    // Home reads beside the shell instead of after it.
    if (typeof document !== "undefined" && isPlatformShell((await parentMatchPromise).loaderData))
      return loadPlatformHome(deps.flow);
    const data = await loadHome();
    if (data.kind === "platform") return loadPlatformHome(deps.flow);
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    if (loaderData?.kind === "platform") return platformHomeHead(matches, loaderData);
    const head = documentHead(
      matches,
      loaderData?.kind === "page" ? loaderData : { kind: "native" },
      "/",
      {
        title: (shell) => `${shell.creator.name}: your membership home`,
        description: (shell) => shell.creator.description,
      },
    );
    return loaderData?.kind === "home"
      ? homeStylesheets(loaderData.home).reduce(
          (result, href) => withStylesheet(result, href),
          head,
        )
      : head;
  },
  component: Home,
});

function Home() {
  const data = Route.useLoaderData();
  if (data.kind === "platform") return <PlatformHome data={data} />;
  return <CreatorSiteHome data={data} />;
}

function CreatorSiteHome({
  data: initial,
}: {
  data: Exclude<ReturnType<typeof Route.useLoaderData>, { kind: "platform" }>;
}) {
  const shell = useShell();
  const location = useLocation();
  const { data } = usePublishedData(initial, shell, "/", "page");
  if (data.kind === "page") return <DocumentContent data={data} shell={shell} />;
  // The League Bible home's patch-notes forms answer with ?notice= or ?error= in the address.
  const search = new URLSearchParams(location.searchStr);
  return <CreatorHome home={data.home} notice={search.get("notice")} error={search.has("error")} />;
}
